<script setup>
/**
 * The line under the email field while a new address waits to be confirmed
 * (XN-4561). The address they sign in with has not changed yet, so the line
 * says which one still works. "Send it again" asks for the same address
 * again — SSO mails a fresh link and the old one stops working — and Cancel
 * withdraws the change. One request at a time, each answered in the line's
 * own live region. The page owns the details; this hands up what came back.
 *
 * It sits inside the form, so its buttons are type="button": a submit here
 * would save whatever the person had half-typed above it.
 */
import { ref } from 'vue';
import { cancelEmailChange, updateMyDetails } from '../../api/portal';

const props = defineProps({
  // details.email_change: { to, to_masked, expires_at }
  change: { type: Object, required: true },
  // The address they sign in with until the link is used.
  current: { type: String, required: true },
});
const emit = defineEmits(['saved', 'cancelled']);

const busy = ref(false);
const said = ref('');
const failed = ref(false);

async function once(request) {
  if (busy.value) return;
  busy.value = true;
  said.value = '';
  failed.value = false;
  try {
    await request();
  } finally {
    busy.value = false;
  }
}

const sendAgain = () => once(async () => {
  try {
    const { errors, ...fresh } = await updateMyDetails({ email: props.change.to });
    emit('saved', fresh);
    // Refused inside a 200 (too many this hour, or the address taken since): its reason, here.
    const refused = errors?.email?.[0];
    said.value = refused ?? 'Sent again.';
    failed.value = !!refused;
  } catch {
    said.value = "Couldn't send — try again.";
    failed.value = true;
  }
});

const cancel = () => once(async () => {
  try {
    await cancelEmailChange();
    emit('cancelled');
  } catch {
    said.value = "Couldn't cancel — try again.";
    failed.value = true;
  }
});
</script>

<template>
  <div class="pdet-pending" data-testid="details-email-pending">
    <p class="pdet-pending__line">We've sent a link to <b>{{ change.to_masked }}</b>. Until you confirm, keep signing in with <b>{{ current }}</b>.</p>
    <div class="pdet-pending__acts">
      <button type="button" class="pdet-pending__act" :aria-disabled="busy ? 'true' : undefined" @click="sendAgain">Send it again</button>
      <span aria-hidden="true">·</span>
      <button type="button" class="pdet-pending__act" :aria-disabled="busy ? 'true' : undefined" @click="cancel">Cancel</button>
      <span class="pdet-pending__said" :class="{ 'is-failed': failed }" aria-live="polite">{{ said }}</span>
    </div>
  </div>
</template>

<style scoped>
/* The prototype's "Didn't get it? Send it again" line on sign-up's code screen,
   build 30 Sep 12:40: 12.5px and muted, the address bold, the action bold in the brand colour. */
.pdet-pending { margin-top: 8px; font-size: 12.5px; line-height: 1.5; color: var(--xl-color-outline); }
.pdet-pending__line { margin: 0; overflow-wrap: anywhere; }
.pdet-pending__line b { font-weight: 600; color: var(--xl-color-on-surface); }
.pdet-pending__acts { display: flex; flex-wrap: wrap; align-items: baseline; gap: 6px; margin-top: 4px; }
.pdet-pending__act {
  padding: 0;
  border: 0;
  background: none;
  font: inherit;
  font-weight: 600;
  color: var(--xl-color-primary);
  cursor: pointer;
}
.pdet-pending__act[aria-disabled="true"] { opacity: 0.6; cursor: progress; }
.pdet-pending__act:focus-visible { outline: 2px solid var(--xl-color-primary); outline-offset: 2px; border-radius: 4px; }
.pdet-pending__said { margin-left: 4px; }
.pdet-pending__said.is-failed { color: var(--xl-color-error); }
</style>
